import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { PersonDiscs } from "../components/PersonDiscs.js";
import { api } from "../lib/api.js";
import { addDays } from "../lib/dates.js";
import { spanMeta } from "../lib/span.js";
import { useSession } from "../lib/session.js";
import { tintOf } from "../tokens/palette.js";
import type { Occurrence } from "../lib/types.js";
import { occurrencesOn } from "./Week.js";

/**
 * One day, reached by tapping a month cell or a day stamp.
 *
 * Events become tinted cards with a 5px person edge — more room than the week
 * list, so the meta line can carry time, place and repeat in one read.
 */
export function Day(): React.JSX.Element {
  const { zone, canEdit } = useSession();
  const { date = "" } = useParams();
  const navigate = useNavigate();

  const [occurrences, setOccurrences] = useState<Occurrence[] | null>(null);

  const load = useCallback(async () => {
    const data = await api.events(date, addDays(date, 1, zone));
    setOccurrences(occurrencesOn(data.occurrences, date, zone));
  }, [date, zone]);

  useEffect(() => {
    setOccurrences(null);
    void load();
  }, [load]);

  const d = DateTime.fromISO(date, { zone });

  return (
    <div className="screen">
      <div className="day__back">
        <Link to="/">← week</Link>
      </div>

      <header className="dayview__head">
        <h1>
          {d.toFormat("cccc")} <span className="dayview__num">{d.toFormat("d")}</span>
        </h1>
        <span className="dayview__month">{d.toFormat("LLLL yyyy")}</span>
      </header>

      {occurrences !== null && occurrences.length === 0 && (
        <div className="empty">
          <p className="empty__head">Nothing today</p>
          <p className="empty__sub">
            {canEdit ? "Good. Add something if that changes." : "Good."}
          </p>
          {canEdit && (
            <button
              type="button"
              className="button-ink"
              onClick={() => void navigate(`/event/new?date=${date}`)}
            >
              Add event
            </button>
          )}
        </div>
      )}

      <div className="cards">
        {(occurrences ?? []).map((occurrence) => {
          const lead = occurrence.people[0];
          const meta = [
            // Says which day of the run this is, so the card stops being an
            // identical copy of the one on either side of it.
            spanMeta(occurrence, date, zone),
            occurrence.location,
            occurrence.is_recurring ? "repeats" : null,
          ].filter((x): x is string => x !== null && x !== "");

          return (
            <button
              key={occurrence.occurrence_id}
              type="button"
              className={`card${occurrence.is_background ? " is-background" : ""}`}
              style={{
                borderLeftColor: lead?.color ?? "var(--mid)",
                background: lead === undefined ? "var(--paper-raised)" : tintOf(lead.color),
              }}
              onClick={() =>
                void navigate(
                  `/event/${occurrence.event_id}?occ=${encodeURIComponent(occurrence.occurrence_id)}`,
                )
              }
            >
              <PersonDiscs people={occurrence.people} size={20} muted={occurrence.is_background} />
              <span className="card__title">{occurrence.title}</span>
              <span className="card__meta">{meta.join(" · ")}</span>
              {occurrence.notes !== null && <span className="card__notes">{occurrence.notes}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
